import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { isRuntimeProcessorField } from 'helpers/field-config/form-page-helpers';
import { processDynamicField } from 'helpers/field-config/process-dynamic-content';
import { processFields, toQuestion } from 'helpers/field-config/process-static-content';
import { CONFIG_PROCESSORS } from 'helpers/field-config/processors';
import {
  resolveArrayItemTitles,
  resolveArraySize,
  syncArrayFieldItems,
} from 'helpers/field-config/sync-fixed-array';
import { readValueByName } from 'helpers/field-paths';
import { hasFormBasedOptions, resolveOptionsWithFormValues } from 'helpers/options-resolver';
import { componentMap, getComponentProps } from 'helpers/question-config';
import { Controller, useWatch } from 'react-hook-form';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';

const withRelaxedRequiredWhenAlternativeFieldPresent = (field, relax) => {
  if (!relax || !field?.required) return field;
  const alt = field.alternativeField ?? field.alternative_field;
  if (typeof alt !== 'string' || !alt.trim()) return field;
  return { ...field, required: false };
};

const findOptionForValue = (options, selectedValue) => {
  if (selectedValue == null || selectedValue === '' || !Array.isArray(options)) return null;
  return (
    options.find(opt => opt.value === selectedValue) ??
    options.find(opt => String(opt?.value) === String(selectedValue)) ??
    options.find(
      opt => String(opt?.value).trim().toLowerCase() === String(selectedValue).trim().toLowerCase(),
    ) ??
    null
  );
};

const buildFieldDependencyMap = fields => {
  const map = {};
  if (!Array.isArray(fields)) return map;

  for (const field of fields) {
    if (!field?.code) continue;

    const runtimeConfig = field.runtimeConfig || field.runtime_config;
    const dependencyCodes = new Set([
      ...(field.dynamicDependencyCodes || field.dynamic_dependency_codes || []),
      ...(runtimeConfig?.dependsOnFields || runtimeConfig?.depends_on_fields || []),
    ]);

    for (const dep of dependencyCodes) {
      if (typeof dep !== 'string' || dep.includes('.')) continue;
      if (!map[dep]) map[dep] = new Set();
      map[dep].add(field.code);
    }
  }

  return map;
};

const FieldConfigRenderer = ({
  // Required props
  fields,
  control,
  register,
  setValue,
  getValues,
  watch,
  formValuesPrefix,

  // Optional props with defaults
  errors = {},
  company = null,
  enabledCompanyChecks = null,
  companyCheckConfigurations = null,
  companyCheckConfig = null,
  candidate = null,
  checkDetails = null,
  subCheckDetails = null,
  candidateValues = null,
  hideOptionalTag = false,
  titleSize = 'small',
  gap = 'small',
  noMargin = true,
  // Callback for hidden fields - components render them separately (outside ToggleSwitchInput to avoid triggering Collapse)
  onHiddenFieldsRender = null,
  allowEmptyRequiredWhenDataSourceAlternative = false,
  // Company config / completion modal: show required UI but do not enforce required or validator_name
  enforceFieldValidation = true,
  onSelectedOptionsContextChange = null,
  uploadHandlers = {},
  candidateId = null,
}) => {
  const fieldSourcesForProcessing = useMemo(
    () => ({
      company,
      company_teams: company?.teams ?? [],
      enabled_company_checks: enabledCompanyChecks ?? {},
      company_check_configurations: companyCheckConfigurations ?? {},
      configuration: companyCheckConfig,
      candidate,
      candidate_values: candidateValues,
      check_details: checkDetails,
      subcheck_details: subCheckDetails,
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
    ],
  );

  // processFields camelCases field config keys except `code` (snake_case, matches extra_data) and `options`
  // (resolved option rows keep API/join keys). Visibility uses camelCasedFields.
  const camelCasedFields = useMemo(() => {
    return processFields(fields, { sources: fieldSourcesForProcessing });
  }, [fields, fieldSourcesForProcessing]);

  const resolvedDefaultsForConditions = useMemo(() => {
    if (!camelCasedFields?.length) return {};
    const templateContext = { sources: fieldSourcesForProcessing };
    const defaults = {};
    for (const field of camelCasedFields) {
      const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
        field,
        allowEmptyRequiredWhenDataSourceAlternative,
      );
      const question = toQuestion(fieldForQuestion, true, templateContext);
      const def = question.defaultValue ?? field.defaultValue ?? field.default_value;
      if (def !== undefined && def !== null) {
        defaults[field.code] = def;
      }
    }
    return defaults;
  }, [camelCasedFields, fieldSourcesForProcessing, allowEmptyRequiredWhenDataSourceAlternative]);

  // Watch the whole prefix so nested updates (e.g. configuration.multiple_office_codes) reliably
  // trigger visibility re-evaluation; useWatch is more dependable than watch(prefix) alone here.
  const watchedPrefixValues = useWatch({
    control,
    name: formValuesPrefix || undefined,
    defaultValue: undefined,
  });

  const formValues =
    (formValuesPrefix != null && formValuesPrefix !== ''
      ? (watchedPrefixValues ?? watch(formValuesPrefix))
      : watch(formValuesPrefix)) || {};

  const hiddenValues = useMemo(() => {
    const values = {};
    for (const field of camelCasedFields) {
      if (!isRuntimeProcessorField(field)) continue;
      const processorFn = CONFIG_PROCESSORS[field.runtimeConfig.processor];
      if (!processorFn) continue;
      const params =
        field.runtimeConfig.processorParams || field.runtimeConfig.processor_params || {};
      values[field.code] = processorFn(formValues, fieldSourcesForProcessing, params, field);
    }
    return values;
  }, [camelCasedFields, formValues, fieldSourcesForProcessing]);

  const allValues = useMemo(() => ({ ...formValues, ...hiddenValues }), [formValues, hiddenValues]);

  const conditionFormValues = useMemo(() => {
    const merged = {
      ...resolvedDefaultsForConditions,
      company_teams: company?.teams ?? [],
    };
    if (allValues && typeof allValues === 'object') {
      for (const [k, v] of Object.entries(allValues)) {
        if (v !== undefined) {
          merged[k] = v;
        }
      }
    }
    return merged;
  }, [allValues, resolvedDefaultsForConditions, company]);

  // Context for evaluating alternative_source conditions (no selectedOptionsContext to avoid cycle)
  const optionsFilterContext = useMemo(
    () => ({
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
    ],
  );

  // When conditions match, use alternative_source (values or options) as the options list (e.g. volunteer → Probity only)
  const effectiveOptionsMap = useMemo(() => {
    const map = {};
    if (!camelCasedFields?.length) return map;

    for (const field of camelCasedFields) {
      const raw = Array.isArray(field.options) ? field.options : (field.options?.options ?? []);
      const altSource = field.alternative_source ?? field.alternativeSource;

      if (
        altSource?.conditions?.length &&
        evaluateFieldConditions(altSource.conditions, conditionFormValues, optionsFilterContext)
      ) {
        const values = altSource.values ?? [];
        const optionsList = altSource.options;
        if (Array.isArray(optionsList) && optionsList.length > 0) {
          map[field.code] = optionsList;
        } else if (Array.isArray(values) && values.length > 0) {
          map[field.code] = values.map(v => ({ value: v, name: v }));
        } else {
          map[field.code] = raw;
        }
      } else {
        map[field.code] = raw;
      }
    }
    return map;
  }, [camelCasedFields, conditionFormValues, optionsFilterContext]);

  const optionsResolutionContext = useMemo(
    () => ({
      sources: {
        ...fieldSourcesForProcessing,
        ...allValues,
      },
    }),
    [fieldSourcesForProcessing, allValues],
  );

  const optionsLookup = useMemo(() => {
    const lookup = {};
    if (!camelCasedFields?.length) return lookup;

    for (const field of camelCasedFields) {
      const raw = Array.isArray(field.options) ? field.options : (field.options?.options ?? []);
      lookup[field.code] = effectiveOptionsMap[field.code] ?? raw;
    }

    for (const field of camelCasedFields) {
      const optionsConfig = field.options;
      if (Array.isArray(optionsConfig) && hasFormBasedOptions(optionsConfig)) {
        lookup[field.code] = resolveOptionsWithFormValues(
          optionsConfig,
          allValues,
          lookup,
          optionsResolutionContext,
        );
        continue;
      }
      const dottedSource =
        optionsConfig &&
        typeof optionsConfig === 'object' &&
        !Array.isArray(optionsConfig) &&
        typeof optionsConfig.source === 'string' &&
        optionsConfig.source.includes('.');
      if (dottedSource) {
        lookup[field.code] = resolveOptionsWithFormValues(
          optionsConfig,
          allValues,
          lookup,
          optionsResolutionContext,
        );
        continue;
      }
      if (!hasFormBasedOptions(optionsConfig)) continue;
      lookup[field.code] = resolveOptionsWithFormValues(
        optionsConfig,
        allValues,
        lookup,
        optionsResolutionContext,
      );
    }

    return lookup;
  }, [camelCasedFields, effectiveOptionsMap, formValues, allValues, optionsResolutionContext]);

  // Build a context of selected options for template resolution and condition evaluation
  // Maps field code -> selected option object (with all properties like notes, send_destination, etc.)
  // For multi_select, maps to an array of selected option objects (extend_fields available on each).
  const selectedOptionsContext = useMemo(() => {
    const context = {};
    if (!camelCasedFields?.length) return context;

    const findSelectedOption = (options, selectedValue) =>
      findOptionForValue(options, selectedValue);

    for (const field of camelCasedFields) {
      const selectedValue = formValues?.[field.code];
      if (!selectedValue) continue;

      let options = optionsLookup[field.code] || [];
      if (options.length === 0 && selectedValue != null) {
        const otherWithMatch = Object.entries(optionsLookup).find(
          ([code, opts]) =>
            code !== field.code &&
            Array.isArray(opts) &&
            opts.some(
              opt => opt?.value === selectedValue || String(opt?.value) === String(selectedValue),
            ),
        );
        if (otherWithMatch) options = otherWithMatch[1];
      }

      if (field.type === 'multi_select' && Array.isArray(selectedValue)) {
        const selectedOptions = selectedValue
          .map(val => findSelectedOption(options, val))
          .filter(Boolean);
        if (selectedOptions.length > 0) {
          context[field.code] = selectedOptions;
        }
        continue;
      }

      const selectedOption = findSelectedOption(options, selectedValue);

      if (selectedOption) {
        context[field.code] = selectedOption;
      }
    }

    return context;
  }, [camelCasedFields, formValues, optionsLookup, watchedPrefixValues]);

  useEffect(() => {
    onSelectedOptionsContextChange?.(selectedOptionsContext);
  }, [selectedOptionsContext, onSelectedOptionsContextChange]);

  // Build field context for template resolution and condition evaluation
  // Aligns with field_resolver_service data sources: pass `company` through as-is so `company.*`
  // in conditions/templates matches what callers serialize (e.g. admin company payload).
  // Form values are included so {{fieldCode}} in section_help_text (e.g. hidden field codes) resolves in toQuestion.
  // Prefer selectedOptionsContext when a key exists in both, so select/multiple_choice keep option objects and
  // {{fieldCode.name}} etc. still resolve; use formValues only for keys not in selectedOptionsContext (e.g. hidden fields).
  const formAndOptionSources = useMemo(() => {
    const merged = { ...(allValues || {}) };
    const opts = selectedOptionsContext || {};
    for (const [key, opt] of Object.entries(opts)) {
      const field = camelCasedFields?.find(f => f.code === key);
      if (field?.type === 'multi_select') {
        continue;
      }
      merged[key] = opt ?? merged[key];
    }
    return merged;
  }, [allValues, selectedOptionsContext, camelCasedFields]);

  const fieldContext = useMemo(
    () => ({
      sources: {
        company,
        company_teams: company?.teams ?? [],
        enabled_company_checks: enabledCompanyChecks ?? {},
        company_check_configurations: companyCheckConfigurations ?? {},
        configuration: companyCheckConfig,
        candidate,
        candidate_values: candidateValues,
        check_details: checkDetails,
        subcheck_details: subCheckDetails,
        ...hiddenValues,
        ...formAndOptionSources,
      },
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      hiddenValues,
      formAndOptionSources,
    ],
  );

  const conditionContext = useMemo(
    () => ({
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      selectedOptionsContext,
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      selectedOptionsContext,
    ],
  );

  const isFieldVisible = useCallback(
    field => {
      if (!field.conditions) return true;
      try {
        return evaluateFieldConditions(field.conditions, conditionFormValues, conditionContext);
      } catch (error) {
        console.error('Error evaluating field conditions:', error, field);
        return true;
      }
    },
    [conditionFormValues, conditionContext],
  );

  // Check if a field should be treated as hidden due to single option
  // Uses effective options (after alternative_source) so e.g. volunteer -> only Probity -> single option -> hide
  const shouldHideAsSingleOption = useCallback(
    field => {
      const hasOptions = field.options !== undefined && field.options !== null;
      if (!hasOptions) return false;

      const hasHideIfSingleOption = field.hideIfSingleOption;
      if (!hasHideIfSingleOption && !field.required) return false;

      const options = optionsLookup[field.code] ?? [];
      return Array.isArray(options) && options.length <= 1;
    },
    [optionsLookup],
  );

  // Used when splitting visible/hidden and in single-option effect (checks.N.details context)
  const isCheckDetailsPrefix = /^checks\.\d+\.details$/.test(formValuesPrefix || '');

  // Separate hidden fields from visible fields - calculated at render time
  // Fields with hideIfSingleOption and single option are treated as hidden
  // In check details (e.g. ChecksSelector bundle), keep multi_select always visible so we never
  // mount a hidden Controller that could overwrite bundle values with a single-option default
  const visibleFields =
    camelCasedFields?.filter(f => {
      const wouldHide = shouldHideAsSingleOption(f);
      const keepVisibleInCheckDetails = isCheckDetailsPrefix && f.type === 'multi_select';
      return f.type !== 'hidden' && isFieldVisible(f) && (keepVisibleInCheckDetails || !wouldHide);
    }) || [];

  const hiddenFields =
    camelCasedFields?.filter(
      f =>
        (f.type === 'hidden' || shouldHideAsSingleOption(f)) &&
        isFieldVisible(f) &&
        !(isCheckDetailsPrefix && f.type === 'multi_select'),
    ) || [];

  // Identify hidden fields with processors
  const processorFields = useMemo(
    () =>
      camelCasedFields.filter(
        f => isRuntimeProcessorField(f) && hiddenFields.some(h => h.code === f.code),
      ),
    [camelCasedFields, hiddenFields],
  );

  const arraySyncFields = useMemo(
    () =>
      camelCasedFields?.filter(f => {
        const sizeSource = f.sizeSource || f.size_source;
        const syncKey = f.syncKeyField || f.sync_key_field;
        return f.type === 'array' && sizeSource && syncKey;
      }) ?? [],
    [camelCasedFields],
  );

  const formValuesWithSyncedArrays = useMemo(() => {
    if (!arraySyncFields.length) return formValues;
    const merged = { ...formValues };
    for (const field of arraySyncFields) {
      const sizeSource = field.sizeSource || field.size_source;
      const sourceValues = formValues?.[sizeSource];
      const sourceField = camelCasedFields.find(f => f.code === sizeSource);
      merged[field.code] = syncArrayFieldItems(
        field,
        formValues?.[field.code],
        sourceValues,
        merged,
        optionsLookup,
        sourceField,
      );
    }
    return merged;
  }, [arraySyncFields, camelCasedFields, formValues, optionsLookup]);

  const formValuesForArrayConditions = useMemo(() => {
    return { ...hiddenValues, ...formValuesWithSyncedArrays };
  }, [hiddenValues, formValuesWithSyncedArrays]);

  const prevArraySyncRef = useRef({});

  useLayoutEffect(() => {
    if (arraySyncFields.length === 0) return;

    arraySyncFields.forEach(field => {
      const sizeSource = field.sizeSource || field.size_source;
      const sourceValues = formValues?.[sizeSource];
      const sourceField = camelCasedFields.find(f => f.code === sizeSource);
      const fieldPath = `${formValuesPrefix}.${field.code}`;
      const syncKeyValue = JSON.stringify(sourceValues);

      const prevSyncKey = prevArraySyncRef.current[field.code];
      if (syncKeyValue === prevSyncKey) return;

      prevArraySyncRef.current[field.code] = syncKeyValue;
      const currentArray = getValues(fieldPath) ?? formValues?.[field.code];
      const synced = syncArrayFieldItems(
        field,
        currentArray,
        sourceValues,
        formValues,
        optionsLookup,
        sourceField,
      );
      setValue(fieldPath, synced);
    });
  }, [
    arraySyncFields,
    camelCasedFields,
    formValues,
    formValuesPrefix,
    optionsLookup,
    setValue,
    getValues,
  ]);

  // Identify hidden fields from hideIfSingleOption OR required fields with single option - need to auto-select value
  const singleOptionFields = useMemo(() => {
    const filtered = hiddenFields.filter(f => {
      if (f.runtimeConfig?.processor) return false;
      // Include fields with hideIfSingleOption OR required fields that end up with single option
      return f.hideIfSingleOption || f.required;
    });
    return filtered;
  }, [hiddenFields]);

  // Track previous processor values to avoid unnecessary setValue calls
  const prevProcessorValuesRef = useRef({});
  const prevSingleOptionValuesRef = useRef({});

  // Run processors when dependencies change (dynamic value computation)
  useEffect(() => {
    if (processorFields.length === 0) return;

    processorFields.forEach(field => {
      const { processor } = field.runtimeConfig;
      const processorFn = CONFIG_PROCESSORS[processor];

      if (!processorFn) return;

      const params =
        field.runtimeConfig.processorParams || field.runtimeConfig.processor_params || {};
      const result = processorFn(formValues, fieldContext.sources, params, field);

      // Only update if value has changed
      const fieldPath = `${formValuesPrefix}.${field.code}`;
      const prevValue = prevProcessorValuesRef.current[field.code];

      // Compare using JSON for objects/arrays, direct comparison for primitives
      const hasChanged =
        typeof result === 'object'
          ? JSON.stringify(result) !== JSON.stringify(prevValue)
          : result !== prevValue;

      if (hasChanged) {
        prevProcessorValuesRef.current[field.code] = result;
        setValue(fieldPath, result);
      }
    });
  }, [processorFields, formValues, fieldContext, formValuesPrefix, setValue]);

  // Auto-select before paint so hideIfSingleOption fields have a value before dependent fields evaluate.
  useLayoutEffect(() => {
    if (singleOptionFields.length === 0) return;

    singleOptionFields.forEach(field => {
      const options = optionsLookup[field.code] ?? [];
      if (!Array.isArray(options) || options.length !== 1) return;

      const isMultiSelect = field.type === 'multi_select';
      if (isMultiSelect && isCheckDetailsPrefix) return;

      const optionValue = options[0]?.value ?? options[0];
      const singleValue = isMultiSelect ? [optionValue] : optionValue;
      const fieldPath = `${formValuesPrefix}.${field.code}`;
      const prevValue = prevSingleOptionValuesRef.current[field.code];

      const hasChanged =
        typeof singleValue === 'object'
          ? JSON.stringify(singleValue) !== JSON.stringify(prevValue)
          : singleValue !== prevValue;
      if (hasChanged) {
        prevSingleOptionValuesRef.current[field.code] = singleValue;
        setValue(fieldPath, singleValue);
      }
    });
  }, [singleOptionFields, optionsLookup, formValuesPrefix, setValue, isCheckDetailsPrefix]);

  const fieldDependencyMap = useMemo(
    () => buildFieldDependencyMap(camelCasedFields),
    [camelCasedFields],
  );
  const watchedDependencyCodes = useMemo(
    () =>
      Object.keys(fieldDependencyMap).filter(code => {
        const field = camelCasedFields.find(f => f.code === code);
        return !isRuntimeProcessorField(field);
      }),
    [fieldDependencyMap, camelCasedFields],
  );
  const fieldContextSourcesRef = useRef(fieldContext.sources);
  fieldContextSourcesRef.current = fieldContext.sources;

  const resetDependentsForDependency = useCallback(
    changedCode => {
      const dependents = fieldDependencyMap[changedCode];
      if (!dependents) return;

      const currentFormValues = getValues(formValuesPrefix) || {};
      const processorValues = {};
      for (const field of camelCasedFields) {
        if (!isRuntimeProcessorField(field)) continue;
        const processorFn = CONFIG_PROCESSORS[field.runtimeConfig.processor];
        if (!processorFn) continue;
        const params =
          field.runtimeConfig.processorParams || field.runtimeConfig.processor_params || {};
        processorValues[field.code] = processorFn(
          currentFormValues,
          fieldContextSourcesRef.current,
          params,
          field,
        );
      }
      const templateSources = {
        ...fieldContextSourcesRef.current,
        ...currentFormValues,
        ...processorValues,
      };

      for (const fieldCode of dependents) {
        if (fieldCode === changedCode) continue;
        const field = camelCasedFields.find(f => f.code === fieldCode);
        if (!field || field.type === 'hidden') continue;

        const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
          field,
          allowEmptyRequiredWhenDataSourceAlternative,
        );
        const question = toQuestion(fieldForQuestion, true, { sources: templateSources });
        const fieldPath = `${formValuesPrefix}.${fieldCode}`;
        setValue(fieldPath, question.defaultValue ?? undefined, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    },
    [
      fieldDependencyMap,
      camelCasedFields,
      formValuesPrefix,
      setValue,
      getValues,
      allowEmptyRequiredWhenDataSourceAlternative,
    ],
  );

  useEffect(() => {
    if (watchedDependencyCodes.length === 0) return;

    const subscription = watch((_values, { name, type }) => {
      if (!name || type !== 'change') return;

      const relativeName =
        formValuesPrefix && name.startsWith(`${formValuesPrefix}.`)
          ? name.slice(formValuesPrefix.length + 1)
          : formValuesPrefix
            ? null
            : name;
      if (relativeName == null) return;

      const changedCode = relativeName.split('.')[0];
      if (!watchedDependencyCodes.includes(changedCode)) return;

      resetDependentsForDependency(changedCode);
    });

    return () => subscription.unsubscribe();
  }, [watch, watchedDependencyCodes, resetDependentsForDependency, formValuesPrefix]);

  // ============================================================================
  // Rendering
  // ============================================================================

  // Render hidden field with Controller
  const renderHiddenField = useCallback(
    field => {
      const fieldName = `${formValuesPrefix}.${field.code}`;
      const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
        field,
        allowEmptyRequiredWhenDataSourceAlternative,
      );
      const question = toQuestion(fieldForQuestion, true, fieldContext);
      const defaultFromConfig = question.defaultValue ?? field.defaultValue ?? field.default_value;

      // Prefer existing form value (e.g. bundle details) so hidden Controller doesn't overwrite.
      // Use getValues so we read current form state (effect may run with stale formValues closure).
      const currentDetails = formValuesPrefix ? getValues(formValuesPrefix) : undefined;
      const existing = currentDetails?.[field.code] ?? formValues[field.code];
      const hasExistingArray =
        isCheckDetailsPrefix &&
        field.type === 'multi_select' &&
        Array.isArray(existing) &&
        existing.length > 0;

      let defaultValue;
      if (hasExistingArray) {
        defaultValue = existing;
      } else if (field.runtimeConfig?.processor) {
        const processorFn = CONFIG_PROCESSORS[field.runtimeConfig.processor];
        if (processorFn) {
          const params =
            field.runtimeConfig.processorParams || field.runtimeConfig.processor_params || {};
          defaultValue = processorFn(formValues, fieldContext.sources, params, field);
        }
      } else if (field.hideIfSingleOption) {
        const options = optionsLookup[field.code] ?? [];
        if (Array.isArray(options) && options.length === 1) {
          const optionValue = options[0]?.value ?? options[0];
          defaultValue = field.type === 'multi_select' ? [optionValue] : optionValue;
        } else {
          defaultValue = defaultFromConfig;
        }
      } else {
        defaultValue = defaultFromConfig;
      }

      return (
        <Controller
          key={field.code}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          render={() => null}
        />
      );
    },
    [
      formValuesPrefix,
      fieldContext,
      formValues,
      control,
      optionsLookup,
      isCheckDetailsPrefix,
      getValues,
      allowEmptyRequiredWhenDataSourceAlternative,
    ],
  );

  // Call onHiddenFieldsRender callback in useEffect to avoid setState during render
  useEffect(() => {
    if (onHiddenFieldsRender && hiddenFields.length > 0) {
      onHiddenFieldsRender(hiddenFields.map(renderHiddenField));
    }
  }, [onHiddenFieldsRender, hiddenFields, renderHiddenField]);

  // ============================================================================
  // Rendering Helpers
  // ============================================================================

  // Render visible field
  const renderVisibleField = (field, index) => {
    const fieldName = `${formValuesPrefix}.${field.code}`;

    // Check if field has dynamicDependencyCodes for dynamic options
    const fieldDynamicDependencyCodes =
      field.dynamicDependencyCodes ||
      field.dynamic_dependency_codes ||
      field.options?.dynamicDependencyCodes ||
      field.options?.dynamic_dependency_codes ||
      [];
    const hasWatchFields = fieldDynamicDependencyCodes.length > 0;

    let processedField = field;
    let currentFormValues = allValues;

    if (field.type === 'dynamic_array' && field.subFields) {
      processedField = processDynamicField(field, allValues, optionsLookup, fieldContext);
      currentFormValues = allValues;
    } else if (field.type === 'array' && field.subFields) {
      const sizeSource = field.sizeSource || field.size_source;
      if (sizeSource) {
        const titlesSource =
          field.arrayItemTitlesSource || field.array_item_titles_source || sizeSource;
        processedField = {
          ...processedField,
          size: resolveArraySize(formValues, sizeSource),
          arrayItemTitles: resolveArrayItemTitles(
            formValues,
            titlesSource,
            optionsLookup,
            selectedOptionsContext,
          ),
        };
      }
      processedField = processDynamicField(processedField, allValues, optionsLookup, fieldContext);
    } else if (hasWatchFields) {
      const watchFields = fieldDynamicDependencyCodes;
      currentFormValues = { ...allValues };
      watchFields.forEach(f => {
        if (typeof f !== 'string' || f.includes('.')) return;
        const path = formValuesPrefix ? `${formValuesPrefix}.${f}` : f;
        currentFormValues[f] = getValues(path);
      });

      processedField = processDynamicField(field, currentFormValues, optionsLookup, fieldContext);
    }

    const effectiveOptions = optionsLookup[field.code];
    if (Array.isArray(effectiveOptions) && effectiveOptions.length > 0) {
      processedField = { ...processedField, options: effectiveOptions };
    } else if (hasFormBasedOptions(field.options)) {
      processedField = processDynamicField(field, allValues, optionsLookup, fieldContext);
    } else if (
      field.options &&
      typeof field.options === 'object' &&
      !Array.isArray(field.options) &&
      typeof field.options.source === 'string' &&
      field.options.source.includes('.')
    ) {
      processedField = processDynamicField(field, allValues, optionsLookup, fieldContext);
    }

    const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
      processedField,
      allowEmptyRequiredWhenDataSourceAlternative,
    );
    const question = toQuestion(fieldForQuestion, true, fieldContext);

    // Prefer existing form value over config default so pre-filled data (e.g. bundle details in
    // ChecksSelector, or existing values in AdminCompanyCheck/CandidateCompletionModal) is used.
    const existingValue = allValues[field.code];
    let questionWithValue =
      existingValue !== undefined && existingValue !== null
        ? { ...question, defaultValue: existingValue }
        : question;

    if (!enforceFieldValidation) {
      const withoutEnforcedValidation = q => ({
        ...q,
        enforceFieldValidation: false,
        subFields: q.subFields?.map(withoutEnforcedValidation),
      });
      questionWithValue = withoutEnforcedValidation(questionWithValue);
    }

    // Calculate dynamic props based on field type
    const isDynamicArray = field.type === 'dynamic_array' && field.subFields;
    const isFixedArray = field.type === 'array' && field.subFields;
    const isArrayType = ['array', 'dynamic_array'].includes(field.type);
    const isDynamicField = isDynamicArray || isFixedArray || hasWatchFields;

    const allValuesForQuestion =
      isDynamicArray || isFixedArray ? formValuesForArrayConditions : allValues;
    const wrapperNoMargin = isDynamicField ? true : noMargin;
    const wrapperGap = isDynamicField ? 'small' : gap;
    const wrapperHideHeader = isDynamicField ? false : field.type === 'hidden';

    // Extract error from errors object using field name (fallback for React Hook Form Controller)
    const fieldError = readValueByName(errors, fieldName);

    // Key that includes dependency values so statement/text that resolves from selected options re-renders when selection changes
    const dependencyKey =
      hasWatchFields &&
      (field.dynamicDependencyCodes || field.options?.dynamicDependencyCodes)?.length
        ? (field.dynamicDependencyCodes || field.options?.dynamicDependencyCodes)
            .map(dep => formValues?.[dep])
            .filter(Boolean)
            .join('-')
        : isFixedArray
          ? (() => {
              const syncKey = field.syncKeyField || field.sync_key_field;
              return (formValuesWithSyncedArrays[field.code] || [])
                .map(item => (syncKey ? item?.[syncKey] : null))
                .filter(Boolean)
                .join('-');
            })()
          : '';
    const rowKey = dependencyKey ? `${field.code}-${dependencyKey}` : field.code;

    return (
      <div key={rowKey}>
        <QuestionWrapper
          question={questionWithValue}
          questionIndex={index}
          fieldName={fieldName}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          componentMap={componentMap}
          errors={errors}
          error={fieldError}
          hideHeader={wrapperHideHeader}
          hideOptionalTag={hideOptionalTag}
          requirementVariantStyle="text"
          titleSize={titleSize}
          isVisible={true}
          allValues={allValuesForQuestion}
          sectionHeader={questionWithValue.sectionHeader}
          textParams={{ ...formValues, ...hiddenValues }}
          noMargin={wrapperNoMargin}
          gap={wrapperGap}
          enforceFieldValidation={enforceFieldValidation}
          getComponentProps={value =>
            getComponentProps({
              question: questionWithValue,
              value,
              candidateId: candidateId ?? candidate?.id,
              handlers: uploadHandlers,
              notForForm: true,
              noMargin: true,
            })
          }
        />
      </div>
    );
  };

  return (
    <div className="d-flex flex-column u-gap-small">
      {visibleFields.map((field, index) => renderVisibleField(field, index))}
    </div>
  );
};

export default FieldConfigRenderer;
